import { Observable } from "rxjs";
import NetworkService from "../network/networkService";
import { AgTechQueryParam, FilterSchemaType, OdQueryParam } from "../enums";
import { helperFunctions } from "../helper";
import { ITrial, ITrialAllResponse } from "../models/ITrial.model";
import { URLConfig } from "../network/urlStore";
import { IFilterSortAttributesAllResponse, TrialLocationDataAllResponse } from "../models";

export const getTrials = (
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<ITrialAllResponse> => {
  let url = URLConfig.getTrialsUrl();
  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as ITrialAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching user data", error);
      }
    );
  });
};

export const getLocationForTrial = (
  trialId: string,
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<TrialLocationDataAllResponse[]> => {
  let url = URLConfig.getTrialLocations(trialId);
  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as TrialLocationDataAllResponse[];
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching Location for Trial Data", error);
      }
    );
  });
};

export const getContentLengthForTrial = (
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<number> => {
  let url = URLConfig.getTrialsUrl();
  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }

  return new Observable((subscriber) => {
    NetworkService.head(url)
      .then(
        (res: any) => {
          subscriber.next(res?.contentLength ?? 0);
          subscriber.complete();
        },
        (error: Error) => {
          subscriber.error(error);
          console.log("Error while getContentLengthForTrial", error);
        }
      )
      .catch((error: Error) => {
        subscriber.error(error);
        subscriber.next(0);
        console.log("Error while fetching crops master data:", error);
      });
  });
};

export const getTrialById = (
  id: string,
  queryParams?: Array<{
    key: AgTechQueryParam;
    value: string[];
  }>
): Observable<ITrial> => {
  let url = URLConfig.getTrialByIdUrl(id);
  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as ITrial;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching trial data", error);
      }
    );
  });
};

export const getTrialsNextPage = (
  nextPageURI: string
): Observable<ITrialAllResponse> => {
  let url = URLConfig.getTrialsNextPage(nextPageURI);
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res) => {
        if (res) {
          const trialResponse = res as unknown as ITrialAllResponse;
          subscriber.next(trialResponse);
        }
        subscriber.complete();
      },
      (error: any) => {
        subscriber.error(error);
        console.log("Error while fetching Locations master data", error);
      }
    );
  });
};

export const getTrialProductsById = (
  id?: string,
  queryParams?: Array<{
    key: OdQueryParam;
    value: string[];
  }>
): Observable<ITrialAllResponse> => {
  let url = URLConfig.getTrialProductsURL(id);
  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as ITrialAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching trial products data", error);
      }
    );
  });
};

export const getTrialProductsByIdNextPage = (
  nextPageURI: string
): Observable<ITrialAllResponse> => {
  let url = URLConfig.getTrialsNextPage(nextPageURI); // Add this method in URLConfig

  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as ITrialAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching trial products next page data", error);
      }
    );
  });
};

export const getAllTrialProductsById = (
  id?: string,
  queryParams?: Array<{
    key: OdQueryParam;
    value: string[];
  }>
): Observable<ITrial[]> => {
  let allValues: ITrial[] = [];

  return new Observable((subscriber) => {
    const errorHandler = (error: any) => {
      subscriber.error(error);
    };

    const responseHandler = (response: any) => {
      allValues = [...allValues, ...(response.products || [])];

      if (response.nextPageUri) {
        getTrialProductsByIdNextPage(response.nextPageUri).subscribe({
          next: responseHandler,
          error: errorHandler,
          complete: () => {
            console.info("Completed fetching all trial product records");
          },
        });
      } else {
        subscriber.next(allValues);
        subscriber.complete();
      }
    };

    getTrialProductsById(id, queryParams).subscribe({
      next: responseHandler,
      error: errorHandler,
    });
  });
};

export const createTrial = (requestPayload: any): Observable<any> => {
  let url = URLConfig.getTrialsUrl();
  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload).then(
      (trialResponse) => {
        if (trialResponse) {
          const trialsResponse = trialResponse as unknown as ITrialAllResponse;
          subscriber.next(trialsResponse);
        }
        subscriber.complete();
      },
      (error) => {
        subscriber.error(error);
        console.log("Error while creating a Trial", error);
      }
    );
  });
};

export const updateTrial = (
  requestPayload: any,
  trialId: string
): Observable<any> => {
  let url = URLConfig.updateTrialUrl(trialId);

  return new Observable((subscriber) => {
    NetworkService.put(url, requestPayload).then(
      (res) => {
        const response = res as unknown as ITrial;
        subscriber.next(response);
        subscriber.complete();
      },
      (error) => {
        subscriber.error(error);
        console.log("Error while updating trialfield", error);
      }
    );
  });
};

export const updateTrialUsingPatch = (
  requestPayload: any,
  trialId: string
): Observable<any> => {
  let url = URLConfig.updateTrialUrl(trialId);

  return new Observable((subscriber) => {
    NetworkService.patch(url, requestPayload).then(
      (res) => {
        const response = res as unknown as ITrial;
        subscriber.next(response);
        subscriber.complete();
      },
      (error) => {
        subscriber.error(error);
        console.log("Error while updating trialfield", error);
      }
    );
  });
};

export const deleteTrial = (trialId: string): Observable<any> => {
  let url = URLConfig.updateTrialUrl(trialId);

  return new Observable((subscriber) => {
    NetworkService.delete(url).then(
      (response) => {
        subscriber.next(response);
        subscriber.complete();
      },
      (error) => {
        subscriber.error(error);
        console.log("Error while updating trialfield", error);
      }
    );
  });
};

export const addTrialProducts = (
  trialPayload: any,
  id: string
): Observable<ITrial> => {
  let url = URLConfig.addTrialProductsURL(id);
  return new Observable((subscriber) => {
    NetworkService.put(url, trialPayload).then(
      (res) => {
        const response = res as unknown as ITrial;
        subscriber.next(response);
        subscriber.complete();
      },
      (error) => {
        subscriber.error(error);
        console.log("Error while updating field", error);
      }
    );
  });
};
export const updateTrialProducts = (
  RequestPayload: any,
  id: string
): Observable<ITrial> => {
  let url = URLConfig.getTrialProductsURL(id);
  return new Observable((subscriber) => {
    NetworkService.patch(url, RequestPayload).then(
      (res) => {
        const response = res as unknown as ITrial;
        subscriber.next(response);
        subscriber.complete();
      },
      (error) => {
        subscriber.error(error);
        console.log("Error while updating field", error);
      }
    );
  });
};

export const deleteProduct = (
  trialId: string,
  productId: string
): Observable<any> => {
  let url = URLConfig.deleteTrialProductsURL(trialId, productId);
  return new Observable((subscriber) => {
    NetworkService.delete(url).then(
      (response) => {
        subscriber.next(response);
        subscriber.complete();
      },
      (error) => {
        subscriber.error(error);
        console.log("Error while deleting  trial product", error);
      }
    );
  });
};


export const getAllTrials = (
  queryParams?: Array<{ key: AgTechQueryParam | OdQueryParam; value: string[] }>
): Observable<ITrial[]> => {
  let allTrials: ITrial[] = [];

  return new Observable((subscriber) => {
    const errorHandler = (error: any) => {
      subscriber.error(error);
    };

    const responseHandler = (response: ITrialAllResponse) => {
      allTrials = [...allTrials, ...(response.trials || [])];

      if (response.nextPageUri) {
        getTrialsNextPage(response.nextPageUri).subscribe({
          next: responseHandler,
          error: errorHandler,
        });
      } else {
        subscriber.next(allTrials);
        subscriber.complete();
      }
    };

    getTrials(queryParams).subscribe({
      next: responseHandler,
      error: errorHandler,
    });
  });
};

export const getTrialProductsFilterSchema = (
  id?: string,
  queryParams?: Array<{
    key: AgTechQueryParam;
    value: string[];
  }>
): Observable<IFilterSortAttributesAllResponse> => {
  let url = `${ URLConfig.getTrialProductsURL(id)}/filter-schema`;
  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IFilterSortAttributesAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching filter schema", error);
      }
    );
  });
};